import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserService } from '../../../services/user.service';
import { PerfilSeccionId } from '../../data/perfil-microempresario.content';
import { perfilSeccionOrder } from '../perfil-tipo.util';
import { PerfilSolicitudService } from '../solicitud/perfil-solicitud.service';
import { PERFIL_HUB_ROUTE, PERFIL_SECTION_ROUTES } from './perfil-section-nav';

const PERCENT_KEY: Record<PerfilSeccionId, string> = {
  'sobre-ti': 'porcentajeSobreTi',
  'sobre-negocio': 'porcentajeSobreNegocio',
  'sobre-ingresos': 'porcentajeSobreIngresos',
};
const INTRO_SEEN_STORAGE_KEY = 'neocredito.perfil.introsVistos';

@Injectable({ providedIn: 'root' })
export class PerfilSeccionAccesoService {
  constructor(
    private readonly solicitud: PerfilSolicitudService,
    private readonly userService: UserService
  ) {}

  /** Solo solicitud (sin catálogo de docs). Fuente del candado secuencial. */
  ensureFromSolicitud(): Observable<void> {
    return this.solicitud.ensureCargado(true).pipe(map(() => undefined));
  }

  sectionOrder(): PerfilSeccionId[] {
    return perfilSeccionOrder(this.userService.getUser()?.tipoPersona);
  }

  sectionPercent(seccionId: PerfilSeccionId): number {
    const solicitud = this.solicitud.solicitudSnapshot;
    const raw = solicitud?.[PERCENT_KEY[seccionId] as keyof typeof solicitud];
    const n = Number(raw);
    return Number.isFinite(n) ? Math.max(0, Math.min(100, Math.round(n))) : 0;
  }

  /** El intro se muestra una vez por sección y por solicitud, sin depender del %. */
  shouldShowIntro(seccionId: PerfilSeccionId): boolean {
    const marker = this.introSeenMarker(seccionId);
    if (!marker) {
      return true;
    }
    return !this.readSeenIntros().includes(marker);
  }

  markIntroShown(seccionId: PerfilSeccionId): void {
    const marker = this.introSeenMarker(seccionId);
    if (!marker) {
      return;
    }
    const seen = this.readSeenIntros();
    if (seen.includes(marker)) {
      return;
    }
    try {
      sessionStorage.setItem(
        INTRO_SEEN_STORAGE_KEY,
        JSON.stringify([...seen, marker])
      );
    } catch {
      // El modal ya se mostró; continuar aunque el navegador bloquee storage.
    }
  }

  canAccess(seccionId: PerfilSeccionId): boolean {
    const order = this.sectionOrder();
    const idx = order.indexOf(seccionId);
    if (idx < 0) {
      return false;
    }
    if (idx <= 0) {
      return true;
    }
    // Ya empezada o completa: se puede retomar aunque una anterior haya bajado de %.
    if (this.sectionPercent(seccionId) > 0) {
      return true;
    }
    // Empezar sección nueva: todas las anteriores deben estar al 100 %.
    return order
      .slice(0, idx)
      .every((id) => this.sectionPercent(id) >= 100);
  }

  firstBlockedRedirectUrl(): string {
    return PERFIL_HUB_ROUTE;
  }

  sectionRoute(seccionId: PerfilSeccionId): string {
    return PERFIL_SECTION_ROUTES[seccionId] ?? PERFIL_HUB_ROUTE;
  }

  private introSeenMarker(seccionId: PerfilSeccionId): string | null {
    const idSolicitud = this.solicitud.idSolicitud;
    const idEmpresario = this.userService.getUser()?.idEmpresario;
    return idSolicitud && idEmpresario != null
      ? `${idEmpresario}:${idSolicitud}:${seccionId}`
      : null;
  }

  private readSeenIntros(): string[] {
    try {
      const parsed = JSON.parse(
        sessionStorage.getItem(INTRO_SEEN_STORAGE_KEY) ?? '[]'
      );
      return Array.isArray(parsed)
        ? parsed.filter((item): item is string => typeof item === 'string')
        : [];
    } catch {
      return [];
    }
  }
}
